/**
 * user.ts — 사용자/역할/권한 도메인 타입
 *
 * Firebase 의존성 0. Custom Claims 구조와 호환된다.
 */
import type { TenantKey } from "./space";

export type Permission = string;

/** 사용자별 권한 가감 (그룹 권한 위에 덮어쓰기). users/{uid}/perm_overrides/{key}.effect */
export type PermEffect = "allow" | "deny";
export type PermOverrides = Record<string, PermEffect>;

export interface AuthClaims {
  role: string | null;
  is_admin: boolean;
  active: boolean;
}

export interface User {
  uid: string;
  loginId: string;
  name: string;
  email: string;
  role: string | null;
  isAdmin: boolean;
  active: boolean;
  permissions: Permission[];
  /** 전체 메뉴 허용 — 모든 메뉴 게이트(.view) 통과. 업무권한은 permissions로 별도 판정. */
  allMenus: boolean;
  /** 사용자별 개별 권한 가감 (그룹 권한보다 우선: deny > allow > 그룹) */
  overrides: PermOverrides;
  /** 근무 법인(users.tenants — 함수가 관리). 접속 기록 사본·알림 묶음용. 없으면 빈 배열 */
  tenants?: TenantKey[];
}

// ── 내 계정·환경 표시용 (12_통합관리_계정기기설계 §4-2 · §8-D) ──────────────

/** 프로필 사진 data URL 최대 길이 — rules preferences/profile 과 같은 값(§5-2) */
export const PROFILE_PHOTO_MAX_CHARS = 48_000;
/** 프로필 사진 저장 크기(정사각 한 변, px) */
export const PROFILE_PHOTO_SIZE = 128;

/** users/{uid} 중 본인 화면에 보이는 공통 필드(읽기 전용 — 수정은 관리자) */
export interface MyAccountDoc {
  loginId: string;
  name: string;
  phone: string;
  /** 이메일(users.personal_email — 가입 화면이 저장). 본인은 보기만, 변경은 관리자(통합관리 상세) */
  email: string;
  /** 통합 직급(users.rank). 본인은 보기만 */
  rank: string;
  /** "YYYY-MM-DD" (빈 문자열 = 없음) */
  birthDate: string;
  /** 근무 법인 키 목록(users.tenants — 함수가 관리) */
  tenants: TenantKey[];
}

/** 아바타 글자 — 이름 첫 글자, 없으면 아이디 첫 글자 */
export function avatarInitial(name: string | null | undefined, loginId?: string | null): string {
  return name?.trim()?.[0] ?? loginId?.[0]?.toUpperCase() ?? "?";
}

export interface Role {
  id: string;
  label: string;
  permissions: Permission[];
  /** 전체 메뉴 허용 (옵트인). true면 신규 메뉴도 자동 노출. */
  allMenus?: boolean;
  /** 법인 관리자도 이 부서를 배정할 수 있음(사업부 부서 문서 tenant_assignable, 12 §16-2) */
  tenantAssignable?: boolean;
  /** 홈 위젯 on/off(home_widgets, 2026-11-02) — 위젯 키별 false 면 이 부서 홈에서 숨김. 없으면 전부 켜짐 */
  homeWidgets?: Record<string, boolean>;
  /** 부서 기본 홈 배치(home_layout) — 관리자가 홈 편집 모드에서 저장. 없으면 시스템 기본 배치 */
  homeLayout?: RoleHomeLayout | null;
}

/** 부서 기본 홈 배치 — 위젯 목록은 dashboard_prefs DashboardWidget 과 같은 모양(직렬화 대상이라 느슨하게 둔다) */
export interface RoleHomeLayout {
  layout: Record<string, unknown>[];
  /** 저장할 때마다 1 증가 */
  version: number;
  /** "구성원 홈에 지금 적용"으로 저장한 마지막 version. 사용자 문서의 role_layout_applied 가 이보다 작으면 교체 */
  applyVersion: number;
  updatedAt: Date | null;
}

/** 이메일(users.personal_email, 선택) 형식 — 함수 optEmail(backend index.ts)과 같은 식. 로그인 주소(아이디@rmc-internal.local)와 별개 */
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const EMAIL_MAX = 100;
/** 빈 값은 통과(선택 칸). 잘못되면 사용자 문구, 맞으면 null */
export function emailError(v: string, label: string): string | null {
  const t = v.trim();
  if (!t) return null;
  if (t.length > EMAIL_MAX) return `${label}은 ${EMAIL_MAX}자까지 입력할 수 있습니다.`;
  return EMAIL_RE.test(t) ? null : `${label} 형식이 올바르지 않습니다.`;
}
